import { createApi } from "@reduxjs/toolkit/query/react";
import { retryUnlessClientError } from "../retryConditions";
import { createRetryingBaseQuery } from "../retryingBaseQuery";
import type {
  CreateExternalArgs,
  ExternalDetail,
  ExternalDetailsResponse,
  ExternalMutationResponse,
  ExternalMutationResult,
  UpdateExternalRoleArgs,
} from "../../interfaces/External.interface";

const maxRetries = 2;

// POST and PUT /externals/ answer a FlatBuffer caller with Api.External, which
// has no field for role_id or the one-time client secret. Asking for JSON is
// the only way to receive the secret, so these two say so explicitly and the
// base query leaves their Accept alone.
const JSON_ONLY = "application/json";

// A 4xx (a refused role, an out-of-scope client) is the same on every attempt,
// so it is shown at once rather than after the backoff.
const extraOptions = { retryCondition: retryUnlessClientError(maxRetries) };

const baseQuery = createRetryingBaseQuery(
  process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? "",
  maxRetries,
);

/**
 * API clients (LiftCloud Externals), LIFT-1098.
 *
 * Every endpoint here answers JSON, which the FlatBuffer base query's JSON
 * fallback parses: the list is GET /externals/details (the FlatBuffer list at
 * GET /externals/ carries neither organization nor role), and POST/PUT send
 * Accept: application/json so the one-time client secret survives. Bodies go out as JSON too, so
 * role_id keeps its three meanings (id, "" to clear, absent to keep).
 */
export const externalsAPI = createApi({
  reducerPath: "externalsAPI",
  baseQuery,
  keepUnusedDataFor: 0,
  tagTypes: ["tagGetExternals"],
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    //* *********************************************************** */
    //* GET API CLIENTS OF ONE ORGANIZATION */
    getExternalDetails: builder.query({
      query: ({ token, orgId }: { token: string; orgId: string }) => ({
        url: `/externals/details?org_id=${encodeURIComponent(orgId)}`,
        method: "GET",
        headers: { authorization: token },
      }),
      transformResponse: (response: ExternalDetailsResponse): ExternalDetail[] =>
        response?.data ?? [],
      extraOptions,
      providesTags: ["tagGetExternals"],
    }),
    //* *********************************************************** */
    //* CREATE API CLIENT */
    postExternal: builder.mutation({
      query: ({ token, name, orgId, roleId }: CreateExternalArgs) => ({
        url: "/externals/",
        method: "POST",
        body: roleId ? { name, org_id: orgId, role_id: roleId } : { name, org_id: orgId },
        headers: { authorization: token, Accept: JSON_ONLY },
      }),
      transformResponse: (response: ExternalMutationResponse): ExternalMutationResult =>
        response.data,
      extraOptions,
      invalidatesTags: ["tagGetExternals"],
    }),
    //* *********************************************************** */
    //* CHANGE AN API CLIENT'S ROLE */
    putExternalRole: builder.mutation({
      query: ({ token, id, name, roleId }: UpdateExternalRoleArgs) => ({
        url: `/externals/${id}`,
        method: "PUT",
        body: { name, role_id: roleId },
        headers: { authorization: token, Accept: JSON_ONLY },
      }),
      transformResponse: (response: ExternalMutationResponse): ExternalMutationResult =>
        response.data,
      extraOptions,
      invalidatesTags: ["tagGetExternals"],
    }),
    //* *********************************************************** */
    //* DELETE API CLIENT */
    deleteExternal: builder.mutation({
      query: ({ token, id }: { token: string; id: string }) => ({
        url: `/externals/${id}`,
        method: "DELETE",
        headers: { authorization: token },
      }),
      extraOptions,
      invalidatesTags: ["tagGetExternals"],
    }),
  }),
});

export const {
  useGetExternalDetailsQuery,
  useLazyGetExternalDetailsQuery,
  usePostExternalMutation,
  usePutExternalRoleMutation,
  useDeleteExternalMutation,
} = externalsAPI;
